
import {
  Vue, Component, PropSync, Ref, Prop, Watch,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import { PaymentInfoFormData } from '@/features/shipment/types';
import {
  getPostalCodeRules,
  firstAndLastNameRules,
  creditCardRules,
  cvvRules,
  isPostalCodeComplete,
  isPostalCodeValid,
} from '@/features/common/helpers/validationRules';
import { creditCardType } from '@/features/common/types';
import { getCityProvinceResidential } from '@/features/shipment/services/shipment.services';
import { safeListedCountryCodes, monerisEnabledCountryCodes } from '@/features/common/raw';
import { featureNames } from '@/features/common/constants/features';
import { getBooleanFeatureValue } from '@/features/common/helpers/feature-flag';
import PaymentAddressInput from './PaymentAddressInput.vue';

export type PaymentAddressInputRef = InstanceType<typeof PaymentAddressInput>;

@Component({
  components: {
    PaymentAddressInput,
  },
})
export default class Inputs extends Vue {
  @PropSync('cardData', { required: true }) syncedCardData!: PaymentInfoFormData;

  @Prop({ required: true }) formSubmitted!: boolean;

  @Prop({ required: true }) isDateValid!: boolean;

  @Prop({ required: false, default: false }) onlyAddCard!: boolean;

  @Ref('monthInput') monthInput!: HTMLInputElement;

  @Ref('yearInput') yearInput!: HTMLInputElement;

  @Ref('paymentAddressInput') paymentAddressInput!: PaymentAddressInputRef;

  private cvvRules = cvvRules;

  private firstAndLastNameRules = firstAndLastNameRules;

  private isDateInputFocusedOut = false;

  private isMonthFocused = false;

  private isYearFocused = false;

  private shouldShowCityProvinceFields = false;

  private isLookingUpCityProvince = false;

  private currentLookupRequestId: string | null = null;

  private postalCodeLookupTimer: number | null = null;

  private lookupCompleted = false;

  private cityFromLookup = false;

  private provinceFromLookup = false;

  private readonly LOOKUP_DEBOUNCE_DELAY = 300;

  private isMonerisDirectIntegrationEnabled = false;

  get getConditionalRequiredRule(): InputValidationRules {
    return [
      (v: string): string | boolean => {
        if (!this.formSubmitted && !v) return true;
        return (!!v && v.toString().trim().length > 0) || this.$t('labels.required') as string;
      },
    ];
  }

  get cardType(): creditCardType | undefined {
    const visaRegex = /^4/;
    const mcRegex = /^(51|52|53|54|55)/;
    const amexRegex = /^(34|37)/;

    if (!this.syncedCardData) return undefined;

    const { cardNumber } = this.syncedCardData;

    const cardTypeByRegex: {
      type: creditCardType,
      regex: RegExp,
    }[] = [
      { type: 'Visa', regex: visaRegex },
      { type: 'MasterCard', regex: mcRegex },
      { type: 'AmericanExpress', regex: amexRegex },
    ];

    return cardTypeByRegex.find((card) => card.regex.test(cardNumber))?.type;
  }

  get cardIconSrc(): string {
    if (this.cardType) {
      /* eslint-disable global-require */
      return require(`@img/creditCardTypes/${this.cardType}.svg`);
    }

    return require('@icons/icon-credit-card.svg');
  }

  get cardDateValidationMsg(): string {
    if (!this.formSubmitted && !this.syncedCardData.month && !this.syncedCardData.year) return '';
    if (!this.formSubmitted && (this.isMonthFocused || this.isYearFocused)) return '';

    return !this.isDateValid ? this.$t('labels.required') as string : '';
  }

  get cardRules(): InputValidationRules {
    return [
      ...this.getConditionalRequiredRule,
      ...creditCardRules(this.cardType),
    ];
  }

  get allowedCountryCodes(): string[] | null {
    if (this.isMonerisDirectIntegrationEnabled) {
      return monerisEnabledCountryCodes;
    }
    return null;
  }

  async mounted(): Promise<void> {
    const flagValue = await getBooleanFeatureValue(featureNames.useMonerisDirect3ds);
    this.isMonerisDirectIntegrationEnabled = flagValue === true || flagValue === undefined;
  }

  private onDateFocusedOut(type: 'month' | 'year') {
    if (type === 'month') {
      this.isMonthFocused = false;
    } else if (type === 'year') {
      this.isYearFocused = false;
    }
    this.isDateInputFocusedOut = true;
  }

  private handleMonthInput(value: string): void {
    const output = +value > 12 ? '12' : value;

    if (output.length === 2) {
      this.yearInput.focus();
    }

    this.$nextTick(() => {
      this.syncedCardData.month = output;
    });
  }

  private handleYearInput(value: string): void {
    const currentYear = ((new Date()).getFullYear() % 100);
    const currentYearDigits = currentYear.toString().split('');
    let output = value;

    if (+value[0] < +currentYearDigits[0]) {
      output = '';
    } else if (value.length === 2 && +value < currentYear) {
      const [char] = value;
      output = char;
    } else {
      output = value.slice(0, 2);
    }

    this.$nextTick(() => {
      this.syncedCardData.year = output;
    });
  }

  private handleYearKeyDown({ key }) {
    if (key === 'Backspace' && !this.syncedCardData.year) {
      this.monthInput.focus();
    }
  }

  private trimPostalCode() {
    this.syncedCardData.address.postalCode = this.syncedCardData.address.postalCode.trim();
  }

  private resetLookupState() {
    this.lookupCompleted = false;
    this.cityFromLookup = false;
    this.provinceFromLookup = false;
    this.syncedCardData.address.city = '';
    this.syncedCardData.address.province = '';

    const { countryCode } = this.syncedCardData.address;
    if (!countryCode) {
      this.shouldShowCityProvinceFields = false;
      return;
    }

    this.shouldShowCityProvinceFields = !safeListedCountryCodes.includes(countryCode);
  }

  private clearLookupTimer() {
    if (this.postalCodeLookupTimer) {
      clearTimeout(this.postalCodeLookupTimer);
      this.postalCodeLookupTimer = null;
    }
  }

  private cancelCurrentLookup() {
    this.currentLookupRequestId = null;
    this.isLookingUpCityProvince = false;
    this.clearLookupTimer();
  }

  private scheduleLookupIfNeeded() {
    const { postalCode, countryCode } = this.syncedCardData.address;

    if (!postalCode || !safeListedCountryCodes.includes(countryCode)) {
      return;
    }

    const countryInfo = this.$store.getters.getCountryFromCode(countryCode);
    if (isPostalCodeComplete(postalCode, countryCode, countryInfo)) {
      this.clearLookupTimer();
      this.postalCodeLookupTimer = window.setTimeout(
        () => this.triggerLookupIfValid(),
        this.LOOKUP_DEBOUNCE_DELAY,
      );
    }
  }

  private handlePostalCodeInput(val: string) {
    this.syncedCardData.address.postalCode = val && val.toUpperCase();
    this.cancelCurrentLookup();
    this.resetLookupState();
    this.scheduleLookupIfNeeded();
  }

  private async handlePostalCodeFocusOut() {
    this.trimPostalCode();
    this.clearLookupTimer();

    if (this.isLookingUpCityProvince) {
      return;
    }

    if (!this.lookupCompleted) {
      await this.triggerLookupIfValid();
    }
  }

  private async triggerLookupIfValid() {
    const { countryCode, postalCode } = this.syncedCardData.address;

    if (!safeListedCountryCodes.includes(countryCode) || !postalCode) {
      return;
    }

    const countryInfo = this.$store.getters.getCountryFromCode(countryCode);
    if (!isPostalCodeValid(postalCode, countryInfo)) {
      return;
    }

    await this.lookupCityProvince();
  }

  private async lookupCityProvince() {
    const { countryCode, postalCode } = this.syncedCardData.address;

    if (!safeListedCountryCodes.includes(countryCode) || !postalCode) {
      this.shouldShowCityProvinceFields = false;
      return;
    }

    const requestId = `${countryCode}-${postalCode}-${Date.now()}`;
    this.currentLookupRequestId = requestId;
    this.isLookingUpCityProvince = true;

    try {
      const response = await getCityProvinceResidential(countryCode, postalCode);

      if (this.currentLookupRequestId !== requestId) {
        return;
      }

      this.handleLookupResponse(response);
    } catch (error) {
      if (this.currentLookupRequestId !== requestId) {
        return;
      }
      this.handleLookupError();
    } finally {
      if (this.currentLookupRequestId === requestId) {
        this.isLookingUpCityProvince = false;
        this.currentLookupRequestId = null;
      }
    }
  }

  private handleLookupResponse(response: any) {
    const hasCity = Boolean(response && response.city);
    const hasProvince = Boolean(response && response.province);

    this.cityFromLookup = hasCity;
    this.provinceFromLookup = hasProvince;
    this.lookupCompleted = true;

    if (hasCity && hasProvince) {
      this.syncedCardData.address.city = response.city;
      this.syncedCardData.address.province = response.province;
      this.shouldShowCityProvinceFields = false;
    } else {
      this.syncedCardData.address.city = hasCity ? response.city : '';
      this.syncedCardData.address.province = hasProvince ? response.province : '';
      this.shouldShowCityProvinceFields = true;
    }
  }

  private handleLookupError() {
    this.cityFromLookup = false;
    this.provinceFromLookup = false;
    this.lookupCompleted = true;
    this.syncedCardData.address.city = '';
    this.syncedCardData.address.province = '';
    this.shouldShowCityProvinceFields = true;
  }

  @Watch('syncedCardData.address.countryCode')
  private async onCountryCodeChange(newValue: string) {
    if (this.isMonerisDirectIntegrationEnabled) return;

    this.cancelCurrentLookup();
    this.resetLookupState();

    if (!newValue) {
      this.shouldShowCityProvinceFields = false;
      return;
    }

    if (!safeListedCountryCodes.includes(newValue)) {
      this.shouldShowCityProvinceFields = true;
      return;
    }

    if (this.syncedCardData.address.postalCode) {
      await this.triggerLookupIfValid();
    } else {
      this.shouldShowCityProvinceFields = false;
    }
  }

  @Watch('syncedCardData.useShippingAddress')
  private async onUseShippingAddressChange(newValue: boolean) {
    if (this.isMonerisDirectIntegrationEnabled) return;

    if (!newValue && !this.onlyAddCard) {
      await this.triggerLookupIfValid();
    }
  }

  beforeDestroy() {
    if (this.isMonerisDirectIntegrationEnabled) return;

    this.clearLookupTimer();
  }

  private getCityValidationRules(): InputValidationRules {
    const { countryCode } = this.syncedCardData.address;
    const isNonSafeListedCountry = !safeListedCountryCodes.includes(countryCode);

    const needsCity = this.shouldShowCityProvinceFields
      && (isNonSafeListedCountry || (!this.cityFromLookup
      && (this.lookupCompleted || this.formSubmitted)));

    if (!needsCity && !this.formSubmitted) {
      return [];
    }

    return [
      (v: string): string | boolean => {
        if (needsCity && (!v || !v.trim())) {
          return this.$t('labels.required') as string;
        }
        return true;
      },
    ];
  }

  get isLookupInProgress(): boolean {
    return this.isLookingUpCityProvince;
  }

  get canSubmitPaymentForm(): boolean {
    if (this.isLookingUpCityProvince) {
      return false;
    }

    const { countryCode, postalCode } = this.syncedCardData.address;

    if (!safeListedCountryCodes.includes(countryCode)) {
      if (this.shouldShowCityProvinceFields) {
        const cityValid = Boolean(this.syncedCardData.address.city
          && this.syncedCardData.address.city.trim());
        return cityValid;
      }
      return true;
    }

    if (postalCode) {
      if (!this.lookupCompleted) {
        return false;
      }

      if (this.shouldShowCityProvinceFields) {
        const cityValid = Boolean(this.cityFromLookup || (this.syncedCardData.address.city
        && this.syncedCardData.address.city.trim()));
        return cityValid;
      }
    }

    return true;
  }

  private getPostalCodeRules(): InputValidationRules {
    if (!this.formSubmitted && !this.syncedCardData.address.postalCode) return [];

    const { countryCode } = this.syncedCardData.address;
    const countryInfo = this.$store.getters.getCountryFromCode(countryCode);

    if (!countryInfo) {
      return this.getConditionalRequiredRule;
    }

    return getPostalCodeRules(countryInfo);
  }
}
